import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import type { CompanyDto } from "@/api/types";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { RefreshCcw01Icon } from "@/components/Icons/RefreshCcw01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { Text } from "@/design-system/Text";
import { canListAddresses, canManageCompanies } from "@/helpers/permissions";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { companyMutations } from "@/queries/companies/mutations";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

type CompanyTableSortBy = ApiQueryParams<"getCompaniesV1">["SortBy"];

export function CompanyListPage(): React.ReactNode {
  const {
    componentProps: confirmDeleteInactiveCompanyModalProps,
    openDeleteModal: openConfirmDeleteInactiveCompanyModal,
  } = useDeleteModal<CompanyDto>("confirm-delete-inactive-company-modal");
  const { componentProps: confirmDeleteActiveCompanyModalProps, openDeleteModal: openConfirmDeleteActiveCompanyModal } =
    useDeleteModal<CompanyDto>("confirm-delete-active-company-modal");

  const [locationCodeModalCompanyId, setLocationCodeModalCompanyId] = useState<string>();
  const [isRefreshingLocationCode, refreshingLocationCodeHandlers] = useBool();
  const [sortDescending, setSortDescending] = useState(false);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "activatedUsers" | "notActivatedUsers" | undefined>("name");

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const projectId = useProjectId();
  const navigate = useNavigate();
  const debouncedQuery = useDebounceValue(query.toLowerCase().trim());
  const hasPermission = usePermission();

  const companyQueries = useCompanyQueries();
  const {
    data: companiesData,
    isFetchingNextPage: isFetchingMoreCompanies,
    isFetching: isFetchingCompanies,
    hasNextPage: hasMoreCompanies,
    fetchNextPage: loadMoreCompanies,
    error: companiesError,
  } = useInfiniteQuery(companyQueries.listInfinite(debouncedQuery, sortBy, sortDescending));
  const deleteCompany = companyMutations.useDeleteCompany();
  const refreshLocationCode = companyMutations.useRefreshLocationCode();

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreCompanies && hasMoreCompanies) {
        void loadMoreCompanies();
      }
    }, [isFetchingMoreCompanies, hasMoreCompanies, loadMoreCompanies]),
  });

  // Add new mapping for each column that should be sortable
  const sortMapTableToApi = {
    name: "name",
    activeUserCount: "activatedUsers",
    inactiveUserCount: "notActivatedUsers",
  } satisfies Partial<Record<keyof CompanyDto, CompanyTableSortBy>>;
  const sortMapApiToTable = {
    name: "name",
    activatedUsers: "activeUserCount",
    notActivatedUsers: "inactiveUserCount",
  } satisfies Partial<Record<keyof CompanyTableSortBy, keyof CompanyDto>>;
  const sorting =
    sortBy && sortBy in sortMapApiToTable
      ? [
          {
            id: sortMapApiToTable[sortBy],
            desc: sortDescending ?? false,
          },
        ]
      : [];
  const columns = useMemo(() => {
    const sortableIds = Object.keys(sortMapTableToApi);
    const helper = createDashboardColumnHelper<CompanyDto>();

    return [
      helper.accessor("name", {
        header: t("page.companies.list.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        enableSorting: sortableIds.includes("name"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("address", {
        header: t("page.companies.list.address"),
        cell: (cell) => (
          <span>
            {cell.getValue().locatedAt} ({cell.row.original.building.name})
          </span>
        ),
        enableSorting: sortableIds.includes("address"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      ...(hasPermission(canManageCompanies)
        ? [
            helper.accessor("locationCode", {
              header: t("page.companies.list.location-code"),
              cell: (cell) => (
                <Inline gap="sm" align="center" wrap={false}>
                  <span className="truncate">{cell.getValue()}</span>
                  {hasPermission(canManageCompanies) ? (
                    <IconButton
                      variant="ghostPrimary"
                      size="sm"
                      title={t("page.companies.list.refresh-location-code")}
                      onClick={() => setLocationCodeModalCompanyId(cell.row.original.id)}
                      icon={<RefreshCcw01Icon />}
                    />
                  ) : null}
                </Inline>
              ),
              minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
              size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
              enableSorting: sortableIds.includes("locationCode"),
            }),
          ]
        : []),
      helper.accessor("activeUserCount", {
        header: t("page.companies.list.active-user-count"),
        cell: (cell) => cell.getValue(),
        enableSorting: sortableIds.includes("activeUserCount"),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      ...(hasPermission(canManageCompanies)
        ? [
            helper.accessor("inactiveUserCount", {
              header: t("page.companies.list.inactive-user-count"),
              cell: (cell) => cell.getValue(),
              minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
              size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
            }),
            helper.accessor("id", {
              header: "",
              cell: (cell) => {
                const menuActions: ContextMenuItem[] = [
                  {
                    labels: {
                      default: t("common.action.edit"),
                    },
                    value: "edit",
                    icon: <Edit05Icon />,
                    callbackFn: () => {
                      void navigate(routes.companies.edit({ slug, id: cell.getValue() }));
                    },
                    "data-testid": "context-menu-edit-btn",
                    isHidden: !hasPermission(canManageCompanies),
                  },
                  {
                    labels: {
                      default: t("common.action.delete"),
                    },
                    value: "delete",
                    icon: <Trash02Icon />,
                    callbackFn: () => {
                      if (cell.row.original.activeUserCount > 0) {
                        openConfirmDeleteActiveCompanyModal(cell.row.original);
                      } else {
                        openConfirmDeleteInactiveCompanyModal(cell.row.original);
                      }
                    },
                    "data-testid": "context-menu-delete-btn",
                    isHidden: !hasPermission(canManageCompanies),
                  },
                ];

                return <ContextMenu items={menuActions} />;
              },
              size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
              meta: {
                isAction: true,
              },
              enableSorting: false,
              enableResizing: false,
            }),
          ]
        : []),
    ];
  }, [
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the React Compiler memoizes sortMapTableToApi
    sortMapTableToApi,
    t,
    hasPermission,
    navigate,
    slug,
    openConfirmDeleteActiveCompanyModal,
    openConfirmDeleteInactiveCompanyModal,
  ]);

  const companies = useMemo(() => companiesData?.pages.flatMap((x) => x.items) || [], [companiesData?.pages]);
  const totalCompanies = companiesData?.pages?.[0]?.total;

  const tableInstance = useResizableTable(TABLE_KEYS.COMPANIES, {
    columns,
    data: companies,
    state: {
      sorting,
    },
    onSortingChange(updater) {
      const newValue = typeof updater === "function" ? updater(sorting) : updater;
      const newSort = newValue?.[0];

      if (newSort && newSort.id in sortMapTableToApi) {
        const sortId = sortMapTableToApi[newSort.id as keyof typeof sortMapTableToApi];
        setSortBy(sortId);
        setSortDescending(newSort.desc);

        return;
      }

      setSortBy(undefined);
      setSortDescending(false);
    },
    manualSorting: true,
    enableSortingRemoval: false,
  });

  if (companiesError) {
    return <ErrorPage error={companiesError} />;
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.companies.list.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.companies.list.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{
                scope: "project",
                entity: "companies",
                page: "list",
                filters: { search: debouncedQuery },
              }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.companies.suggestion.1"),
                t("component.ask-nobi-chat-drawer.companies.suggestion.2"),
              ]}
            />
            {hasPermission(canManageCompanies) && hasPermission(canListAddresses) && (
              <Button variant="soft" icon={<Download01Icon />} type="link" href={routes.companies.bulkUpload({ slug })}>
                {t("page.companies.list.actions.bulk-upload")}
              </Button>
            )}
            {hasPermission(canManageCompanies) && hasPermission(canListAddresses) && (
              <Button type="link" href={routes.companies.new({ slug })} data-testid="companies-create-link">
                {t("page.companies.list.actions.create")}
              </Button>
            )}
          </>
        }
        header={
          <Spread>
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("page.companies.list.search.placeholder")}
            />
            {totalCompanies != null && (
              <p className="pl-space-sm text-right text-text-secondary">
                {t("page.companies.list.company-count", { count: totalCompanies })}
              </p>
            )}
          </Spread>
        }
      >
        <Stack gap="sm">
          {totalCompanies === 0 && (
            <Card>
              <Text as="p">{debouncedQuery ? t("page.companies.list.no-result") : t("page.companies.list.empty")}</Text>
            </Card>
          )}
          {((totalCompanies && totalCompanies > 0) || isFetchingCompanies) && (
            <Table<CompanyDto>
              getRowHref={(row) => routes.companies.edit({ slug, id: row.original.id })}
              table={tableInstance}
              data-testid="companies-list"
              readOnly={!hasPermission(canManageCompanies)}
              isLoading={isFetchingCompanies && !isFetchingMoreCompanies}
            />
          )}
          {hasMoreCompanies && <CollectionLoadingState ref={ref} />}
        </Stack>
      </DocumentPage>
      <DeleteModal
        title={t("page.companies.list.actions.delete.title")}
        description={t("page.companies.list.actions.delete.description")}
        onDelete={(company) => deleteCompany.mutateAsync(company.id)}
        {...confirmDeleteInactiveCompanyModalProps}
      />
      <DeleteModal
        title={t("page.companies.list.delete-active-company-modal.title")}
        description={t("page.companies.list.delete-active-company-modal.description", {
          count: confirmDeleteActiveCompanyModalProps.modalContext?.activeUserCount ?? 0,
        })}
        deleteBtnProps={{
          text: t("page.companies.list.delete-active-company-modal.btn.confirm"),
        }}
        onDelete={(company) => deleteCompany.mutateAsync(company.id)}
        {...confirmDeleteActiveCompanyModalProps}
      />
      <ConfirmModal
        tone="warning"
        data-testid="location-code-modal"
        title={t("page.companies.list.location-code.refresh.title")}
        description={t("page.companies.list.location-code.refresh.description")}
        isOpened={!!locationCodeModalCompanyId}
        shouldCloseOnEsc
        onReject={() => setLocationCodeModalCompanyId(undefined)}
        onOpenChange={(state) => {
          if (!state) {
            setLocationCodeModalCompanyId(undefined);
          }
        }}
        rejectBtnProps={{
          text: t("page.companies.list.location-code.refresh.actions.cancel"),
        }}
        onResolve={async () => {
          try {
            if (locationCodeModalCompanyId) {
              refreshingLocationCodeHandlers.setTrue();
              await refreshLocationCode.mutateAsync(locationCodeModalCompanyId);
            }
          } finally {
            refreshingLocationCodeHandlers.setFalse();

            setLocationCodeModalCompanyId(undefined);
          }
        }}
        resolveBtnProps={{
          text: t("page.companies.list.location-code.refresh.actions.confirm"),
        }}
        isLoading={isRefreshingLocationCode}
      />
    </>
  );
}
